import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import { api, mediaUrl } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { usePlayer } from "@/store/player";
import { ArtistBadge } from "@/components/ArtistBadge";

export const SearchBar = () => {
  const t = useT();
  const nav = useNavigate();
  const ref = useRef(null);
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [open, setOpen] = useState(false);
  const playTrack = usePlayer((s) => s.playTrack);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "/" && !["INPUT", "TEXTAREA"].includes(document.activeElement?.tagName)) {
        e.preventDefault();
        ref.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    if (!q.trim()) return setRes(null);
    const id = setTimeout(() => api.get("/search", { params: { q } }).then((r) => setRes(r.data)).catch(() => {}), 220);
    return () => clearTimeout(id);
  }, [q]);

  const go = (path) => { setOpen(false); setQ(""); nav(path); };

  return (
    <div className="relative w-full max-w-xl">
      <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" />
      <input ref={ref} data-testid="top-search-input" value={q} onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 180)} placeholder={t("search")}
        className="w-full h-11 pl-11 pr-12 rounded-full bg-white/[0.06] border border-white/10 focus:border-[var(--hub)] focus:bg-white/[0.09] outline-none text-sm transition-colors" />
      <kbd className="absolute right-4 top-1/2 -translate-y-1/2 text-[10px] font-mono text-zinc-500 border border-white/10 rounded px-1.5">/</kbd>
      {open && res && (
        <div data-testid="search-results" className="absolute top-12 inset-x-0 glass rounded-2xl p-2 max-h-[70vh] overflow-auto z-50">
          {!res.artists.length && !res.tracks.length && <div className="p-4 text-sm text-zinc-400">No results for “{q}”</div>}
          {res.artists.length > 0 && <div className="eyebrow px-3 pt-2 pb-1">Artists</div>}
          {res.artists.map((a) => (
            <button key={a.user_id} data-testid={`search-artist-${a.slug}`} onMouseDown={() => go(`/artist/${a.slug}`)} className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 text-left">
              <img src={mediaUrl(a.avatar)} alt="" className="w-9 h-9 rounded-full object-cover bg-zinc-800" />
              <span className="text-sm font-medium">{a.name}</span> <ArtistBadge badge={a.badge} />
            </button>
          ))}
          {res.tracks.length > 0 && <div className="eyebrow px-3 pt-3 pb-1">Tracks</div>}
          {res.tracks.map((tr) => (
            <button key={tr.track_id} data-testid={`search-track-${tr.track_id}`} onMouseDown={() => { playTrack(tr, res.tracks); go(`/track/${tr.track_id}`); }} className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 text-left">
              <img src={mediaUrl(tr.cover_url)} alt="" className="w-9 h-9 rounded object-cover bg-zinc-800" />
              <div className="min-w-0"><div className="text-sm truncate">{tr.title}</div><div className="text-xs text-zinc-500 truncate">{tr.artist?.name}</div></div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
